import { reactive } from 'vue';
import { DossierId } from '@/dossier/models/AbstractDossier';
import JobContext from '@/jobs/models/JobContext';
import MediaData from '@/media/models/MediaData';
import serviceEvents, { MediaRemoved, MediaUploaded } from '@/shared/services/events/serviceEvents';
import { mergeInto, removeBy } from '@/shared/utils';
import { isPresent, present } from '@/shared/utils/typescript';
import MediaFileClient from '../client/MediaFileClient';
import { parseMediaFile, parseMediaTarget } from '../converters/mediaFileConverters';
import { ImageData } from '../models/ImageData';
import MediaContext, { DossierMediaContext, JobMediaContext } from '../models/MediaContext';
import MediaFile from '../models/MediaFile';
import AuthService from './AuthService';
import pushService from './PushService';

class MediaFileService {
  async loadForDossier(dossierId: DossierId): Promise<DossierMediaContext> {
    const list = await MediaFileClient.allByDossier(dossierId);
    const context = reactive(new DossierMediaContext(dossierId, list));
    this.startListUpdateListener(context);
    return context;
  }

  async loadForJob(context: JobContext): Promise<JobMediaContext> {
    const mediaContext = reactive(new JobMediaContext(context.getSelectedJob(), context.dossier.id));
    this.startListUpdateListener(mediaContext);
    return mediaContext;
  }

  async create(formData: MediaData, context: MediaContext): Promise<void> {
    formData.files.map(() => context.addPlaceholder({
      description: formData.description,
      isLocked: !formData.sharedWithClient,
      isWarranty: formData.isWarranty,
      target: context.target,
    }));
    await MediaFileClient.createMedia(formData, context);
  }

  startListUpdateListener(context: MediaContext): void {
    context.freeHooks.push(pushService.bind(`dossiers-${context.dossierId}/media-uploaded`, (event) => {
      const file = present(parseMediaFile(event.media));
      const target = parseMediaTarget(event);
      file.target = target.type; // push event doesn't contain target

      // TODO there is currently no better way to find the placeholder that corresponds to incoming pusher mediafile.
      // POST dossiers/dossierId/attachments does not return contentId of the new file
      // TODO this.list is not correct for job + diagnostic ???
      if (file.createdBy?.id === AuthService.getCurrentUser().id) {
        const placeholder = context.placeholders.shift();
        placeholder?.updateWith(file);
      }

      // all media are also added to dossier
      mergeInto(file, context.list, f => f.contentId === file.contentId);

      serviceEvents.fire(new MediaUploaded(file, target));
    }));
  }

  async edit(mediaFiles: Array<MediaFile>, locked?: boolean, warranty?: boolean): Promise<void> {
    await MediaFileClient.edit(mediaFiles, locked, warranty);
    mediaFiles.forEach(mediaFile => mediaFile.isLocked = isPresent(locked) ? locked : mediaFile.isLocked);
    mediaFiles.forEach(mediaFile => mediaFile.isWarranty = isPresent(warranty) ? warranty : mediaFile.isWarranty);
  }

  async remove(mediaFiles: Array<MediaFile>, context: MediaContext): Promise<void> {
    await MediaFileClient.remove(mediaFiles);
    mediaFiles.forEach(file => {
      removeBy(context.list, f => f.contentId === file.contentId);
      serviceEvents.fire(new MediaRemoved(file, context.asTarget()));
    });
  }

  async updateNote(file: MediaFile, note: string): Promise<void> {
    await MediaFileClient.updateNote(present(file.contentId), present(file.target), note);
    file.note = note;
  }
}

export default new MediaFileService();